import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Button, message, Select, Spin, Upload } from "antd";
import axios from "axios";
import { BASE_URL } from "../constants/Constants";
import { UploadOutlined } from "@ant-design/icons";

const { Option } = Select;

const AddProductForm = ({ visible, onClose, onSuccess, product }) => {
  const [form] = Form.useForm();
  const [subcategories, setSubcategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [productIdExists, setProductIdExists] = useState(false);
  
  const [fileList, setFileList] = useState([]);

  const checkProductId = (productId) => {
    if (product && Array.isArray(product)) {
      const exists = product.some((p) => String(p.productId) === String(productId));
      setProductIdExists(exists);
      return exists;
    }
    return false;
  };

  useEffect(() => {
    if (visible) {
        fetchCategories();
        setFileList([]); 
    }
  }, [visible]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/category/all-unhide`);
      setCategories(res?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCategoryChange = async (categoryName) => {
    const selectedCategory = categories.find((c) => c.categoryName === categoryName);
    
    if (selectedCategory && selectedCategory.categoryId) {
        try {
            const res = await axios.get(`${BASE_URL}/api/subcategories/by-category/${selectedCategory.categoryId}`);
            setSubcategories(res.data || []);
        } catch (error) {
            console.error("Error fetching subcategories:", error);
            setSubcategories([]);
        }
    } else {
        setSubcategories([]);
    }
    form.setFieldsValue({ subcategories: undefined });
  };

  const handleFileChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const cleanDescription = (text) => {
      if (!text) return "";
      return text.split('\n')
          .map(line => line.replace(/^[\s•\-\*]+/, '')) 
          .join('\n');
  };

  const handleAddProduct = async () => {
    try {
      const values = await form.validateFields();

      if (productIdExists) {
        message.error("Product ID already exists locally");
        return;
      }

      setIsLoading(true);

      const selectedCatObj = categories.find(c => c.categoryName === values.productCategories);
      const selectedSubObj = subcategories.find(s => s.subCategoryName === values.subcategories);

      const cleanShortDesc = cleanDescription(values.productShortDescription);
      const cleanLongDesc = cleanDescription(values.productDescription);

      const productData = {
        productId: values.productId,
        productName: values.productName,
        productPrice: values.productRate ? parseFloat(values.productRate) : 0, 
        mrp: values.productMRP ? parseFloat(values.productMRP) : 0, 
        productStock: values.productStock ? parseFloat(values.productStock) : 0,
        
        // Shipping Charge
        shippingCharge: values.shippingCharge ? parseFloat(values.shippingCharge) : 0.0,

        productQuantity: values.productQuantity ? String(values.productQuantity) : "3", 
        
        category: values.productCategories, 
        categoryId: selectedCatObj ? selectedCatObj.categoryId : null, 
        subcategoryId: selectedSubObj ? selectedSubObj.subCategoryId : null, 

        brand: values.productBrand,
        prdDesc: cleanLongDesc, 
        shortDesc: cleanShortDesc,
        productHidden: false, 
        inactive: false,
        // ➤ CHANGED: We removed the input, so we just send 0 by default.
        priority: 0, 
      };

      await axios.post(`${BASE_URL}/products/create`, productData);

      if (fileList.length > 0) {
          const formData = new FormData();
          fileList.forEach(file => {
             formData.append("photos", file.originFileObj);
          });

          try {
             const uploadRes = await axios.post(`${BASE_URL}/productphotos/upload/${values.productId}`, formData, {
                 headers: { 'Content-Type': 'multipart/form-data' }
             });
             if (uploadRes.status === 200) {
                 message.success("Product and Photos uploaded successfully!");
             }
          } catch (photoError) {
             if (photoError.response && photoError.response.status === 200) {
                 message.success("Product and Photos uploaded successfully!");
             } else {
                 message.warning("Product added, but photo upload failed.");
             }
          }
      } else {
         message.success("Product added successfully (No photos)");
      }

      form.resetFields();
      setFileList([]); 
      if (onSuccess) onSuccess();
      else onClose();

    } catch (error) {
      console.error(error);
      message.error("Failed to add product. " + (error.response?.data?.message || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      title="Add Product"
      visible={visible}
      onCancel={onClose}
      width={700}
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button key="add" type="primary" onClick={handleAddProduct} disabled={productIdExists}>
          {isLoading ? <Spin /> : "Add Product"}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <div style={{ display: 'flex', gap: '20px' }}>
            <div style={{ flex: 1 }}>
                <Form.Item
                label="Product ID"
                name="productId"
                rules={[{ required: true, message: "Product ID is required" }]}
                help={productIdExists ? "ID already exists" : ""}
                validateStatus={productIdExists ? "error" : ""}
                >
                <Input onChange={(e) => checkProductId(e.target.value)} />
                </Form.Item>
            </div>
            <div style={{ flex: 1 }}>
                 <Form.Item label="Product Name" name="productName" rules={[{ required: true }]}>
                    <Input />
                </Form.Item>
            </div>
        </div>

        <Form.Item label="Product Images">
            <Upload listType="picture-card" fileList={fileList} onChange={handleFileChange} beforeUpload={() => false} multiple={true} accept="image/*">
                {fileList.length >= 8 ? null : (<div><UploadOutlined /><div style={{ marginTop: 8 }}>Upload</div></div>)}
            </Upload>
        </Form.Item>

        {/* ➤ REMOVED PRIORITY FIELD FROM HERE */}

        <Form.Item 
            label="Key Features" 
            name="productShortDescription"
            extra="Enter each feature on a new line. We will add the bullets automatically."
        >
          <Input.TextArea rows={5} placeholder="High quality&#10;Long lasting&#10;Water resistant" />
        </Form.Item>
        
        <Form.Item 
            label="Detailed Description" 
            name="productDescription"
            extra="Enter each point on a new line. Do not add symbols like '-' or '•'."
        >
          <Input.TextArea rows={6} placeholder="Detailed point 1&#10;Detailed point 2" />
        </Form.Item>

        <div style={{ display: 'flex', gap: '20px' }}>
            <Form.Item style={{ flex: 1 }} label="Category" name="productCategories" rules={[{ required: true, message: "Please select a category!" }]}>
            <Select onChange={handleCategoryChange} placeholder="Select Category">
                {categories.map((c) => (<Option key={c.categoryId} value={c.categoryName}>{c.categoryName}</Option>))}
            </Select>
            </Form.Item>

            <Form.Item style={{ flex: 1 }} label="Sub Category" name="subcategories">
            <Select placeholder="Select Sub Category" disabled={subcategories.length === 0}>
                {subcategories.map((s) => (<Option key={s.subCategoryId} value={s.subCategoryName}>{s.subCategoryName}</Option>))}
            </Select>
            </Form.Item>
        </div>

        <div style={{ display: 'flex', gap: '20px' }}>
            <Form.Item style={{ flex: 1 }} label="Selling Price" name="productRate" rules={[{ required: true }]}><Input type="number" /></Form.Item>
            <Form.Item style={{ flex: 1 }} label="MRP" name="productMRP"><Input type="number" /></Form.Item>
            <Form.Item style={{ flex: 1 }} label="Stock" name="productStock" rules={[{ required: true }]}><Input type="number" /></Form.Item>
        </div>
        
         <div style={{ display: 'flex', gap: '20px' }}>
            <Form.Item style={{ flex: 1 }} label="Brand" name="productBrand"><Input /></Form.Item>
            
            <Form.Item 
                style={{ flex: 1 }} 
                label="Max Pack Quantity" 
                name="productQuantity"
                extra="E.g. Enter '6' to allow buying Packs of 1, 2, ... up to 6."
            >
                <Input type="number" placeholder="Default is 3" />
            </Form.Item>

            <Form.Item 
                style={{ flex: 1 }} 
                label="Shipping Charge (₹)" 
                name="shippingCharge"
                initialValue={0}
                extra="Per item cost. Leave 0 for free shipping."
            >
                <Input type="number" placeholder="0" />
            </Form.Item>
         </div>
      </Form>
    </Modal>
  );
};

export default AddProductForm;